import { useParams, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet";
import { orderService } from "/app/src/services";
import { useTranslation } from "react-i18next";
import { OrderLine, Order as OrderType } from "/app/src/models";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useHandleNavigationError } from "/app/src/helpers/api";
import { toast } from "sonner";
import React, { useCallback, useState } from "react";
import ModalBuilder from "/app/src/components/HeroUi/helpers/ModalBuilder";
import { useDisclosure, Button, Modal } from "@heroui/react";
import Breadcrumbs from "/app/src/components/generic/components/breadcrumbs";
import InfoList from "/app/src/components/generic/components/explorer/infoList";
import { PowerPickTimeZoneToLocal } from "/app/src/components/generic/formatting/dateTime";
import ModelItem from "/app/src/components/generic/components/explorer/modelItems";

// Small set of structural Order keys to ignore when detecting dynamic fields.
// Keep this list concise — keys not in this set will be treated as dynamic.
const ORDER_DYNAMIC_IGNORE_KEYS = new Set<string>([
  "id",
  "name",
  "status",
  "allocationStatus",
  "incompleteAllocations",
  "owner",
  "priority",
  "modifiedDate",
  "handlingUnit",
  "hasStock",
  "storageUnits",
  "Info1",
  "Info2",
  "Info3",
  "Info4",
  "Info5",
  "info1",
  "info2",
  "info3",
  "info4",
  "info5",
  "order_lines",
  "orderLinesCount",
  "directionType",
  "unit",
  "recipient",
  "deadline",
  "initialHandlingUnit",
  "warehouseName",
  "type",
  "stationName",
  "typeDescription",
]);

/**
 * Order component for displaying details of a single order.
 *
 * @returns {JSX.Element} The rendered order details component.
 */
export default function Order() {
  const { t } = useTranslation();
  const { orderId } = useParams();
  const navigate = useNavigate();
  const handleNavigationError = useHandleNavigationError();
  const queryClient = useQueryClient();
  const [allocating, setAllocating] = useState(false);
  const [deallocating, setDeallocating] = useState(false);
  const { isOpen, onOpen, onOpenChange } = useDisclosure();

  //Get the Single Order from URL
  // state to hold dynamic (unknown) fields found on the fetched order
  // use 'label' to match the dynamic field objects created below
  const [dynamicFields, setDynamicFields] = useState<
    Array<{ label: string; value: string }>
  >([]);

  const { data: order } = useQuery({
    queryKey: ["order", orderId],
    queryFn: () => {
      return orderService.getSingle(orderId).catch(() => {
        handleNavigationError();
      });
    },
    initialData: { order: {} },
    retry: false,
    select: (data: { order: OrderType }) => {
      return data.order;
    },
    onSuccess: (fetchedOrder: OrderType | undefined) => {
      // When the order is fetched, inspect its keys and capture any keys
      // that are not part of the known Order model as dynamic fields.
      if (!fetchedOrder) return;

      const newDynamicFields = Object.entries(fetchedOrder)
        .filter(
          ([key, value]) =>
            !ORDER_DYNAMIC_IGNORE_KEYS.has(key) && value !== null,
        )
        .map(([key, value]) => ({ label: key, value: value as string }));

      setDynamicFields(newDynamicFields);
    },
  });

  const allocationStatus = order?.allocationStatus as string | undefined;
  const isNotAllocated =
    allocationStatus === "Not Allocated" || allocationStatus === "Not Allocate";
  const isAllocatedOrPartial =
    allocationStatus === "Fully Allocated" ||
    allocationStatus === "Partially Allocated";
  const allocateLabel = isAllocatedOrPartial
    ? t("translation:reallocate")
    : t("translation:allocate");

  const onAllocate = useCallback(async () => {
    if (!orderId) return;
    try {
      setAllocating(true);
      const result = await orderService.allocate(orderId);
      if (result?.fullyAllocated) {
        const hasCount = typeof result?.allocatedLines === "number";
        if (hasCount) {
          toast.success(
            t("translation:order_allocated_success_with_lines", "", {
              count: result.allocatedLines,
            }),
          );
        } else {
          toast.success(t("translation:order_allocated_success"));
        }
      } else {
        const msg =
          result?.message ||
          (result?.status
            ? t("translation:allocation_incomplete_with_status", "", {
                status: result.status,
              })
            : t("translation:order_allocation_unsuccessful"));
        toast.error(msg);
      }
      // refresh order details
      queryClient.invalidateQueries({ queryKey: ["order", orderId] });
    } catch (e) {
      const msg =
        e?.response?.data?.message ||
        e?.message ||
        t("translation:order_allocation_failed");
      toast.error(msg);
    } finally {
      setAllocating(false);
    }
  }, [orderId, queryClient, t]);

  const onDeallocate = useCallback(async () => {
    if (!orderId) return;
    try {
      setDeallocating(true);
      const result = await orderService.deallocate(orderId);
      if (result?.deallocated) {
        toast.success(
          result?.message || t("translation:order_deallocated_success"),
        );
      } else {
        toast.error(
          result?.message || t("translation:order_deallocation_unsuccessful"),
        );
      }
      // refresh order details
      queryClient.invalidateQueries({ queryKey: ["order", orderId] });
    } catch (e) {
      const msg =
        e?.response?.data?.message ||
        e?.message ||
        t("translation:order_deallocation_failed");
      toast.error(msg);
    } finally {
      setDeallocating(false);
    }
  }, [orderId, queryClient, t]);

  const onConfirmDelete = useCallback(async () => {
    if (!orderId) return;
    try {
      await orderService.deleteSingle(orderId);
      onOpenChange();
      toast.success(t("translation:order_deleted_success", "Order deleted"));
      // refresh list and navigate back
      queryClient.invalidateQueries({ queryKey: ["orders"] });
      navigate("/explorer/orders");
    } catch (e) {
      const msg =
        e?.response?.data?.message || e?.message || t("translation:error");
      toast.error(msg);
    }
  }, [orderId, t, queryClient, navigate, onOpenChange]);

  /**
   * Map numeric direction type to a translated label.
   * @param directionType numeric direction type
   * @returns translated string label or '-' when unknown
   */
  const getDirectionType = (directionType: number | undefined) => {
    switch (directionType) {
      case 1:
        return t("translation:put");
      case 2:
        return t("translation:pick");
      case 3:
        return t("translation:return");
      case 4:
        return t("translation:transport");
      case 5:
        return t("translation:count");
      case 7:
        return t("translation:production");
      default:
        return "-";
    }
  };

  /**
   * Map numeric priority to a translated label.
   * @param priority numeric priority
   * @returns translated string label or '-' when unknown
   */
  const getPriorityType = (priority: number | undefined) => {
    switch (priority) {
      case 1:
        return t("translation:low");
      case 2:
        return t("translation:medium");
      case 3:
        return t("translation:high");
      case 4:
        return t("translation:hot");
      default:
        return "-";
    }
  };

  /**
   * Build a simplified list of order line info for display.
   * @returns array of objects with name, id and value for each order line
   */
  const getOrderLineInfo = () => {
    if (!order?.order_lines || order.order_lines.length === 0) return [];
    return order.order_lines.map((line: OrderLine) => {
      return {
        name: `Line #${line.number}`,
        id: line.id,
        value: `${line.materialName} / ${line.quantity}`,
      };
    });
  };

  const basicInfo = [
    { label: t("translation:name"), value: order?.name },
    {
      label: t("translation:direction_type"),
      value: getDirectionType(order?.directionType),
    },
    {
      label: t("translation:creation_date"),
      value: (
        <PowerPickTimeZoneToLocal
          date={order?.modifiedDate}
          format={"MMM DD, YYYY HH:mm"}
        />
      ),
    },
    { label: t("translation:status"), value: order?.status },
    {
      label: t("translation:priority"),
      value: getPriorityType(Number(order?.priority)),
    },
    {
      label: t("translation:type"),
      value: order?.typeDescription,
    },
    { label: t("translation:deadline"), value: order?.deadline },
  ];

  const infoFields = [
    { label: t("translation:info1"), value: order?.Info1 },
    { label: t("translation:info2"), value: order?.Info2 },
    { label: t("translation:info3"), value: order?.Info3 },
    { label: t("translation:info4"), value: order?.Info4 },
    { label: t("translation:info5"), value: order?.Info5 },
  ];

  const orderMetrics = [
    {
      label: t("translation:allocation_status"),
      value: order?.allocationStatus,
    },
    {
      label: t("translation:lines_number"),
      value: order?.orderLinesCount,
    },
  ];

  return (
    <div className="explorer">
      <Helmet>
        <title>{order?.name}</title>
      </Helmet>
      <Breadcrumbs
        items={[
          { label: "Explorer", to: "/explorer" },
          { label: "Orders", to: "/explorer/orders" },
          { label: order?.name },
        ]}
      />
      <div className="flex items-center justify-between">
        <h1 className="text-[36px] font-medium">{order?.name}</h1>
        <div className="gap-2 flex">
          <Button
            color="primary"
            isLoading={allocating}
            onPress={onAllocate}
            disabled={!orderId}
          >
            {allocateLabel}
          </Button>
          {!isNotAllocated && (
            <Button
              color="danger"
              isLoading={deallocating}
              onPress={onDeallocate}
              disabled={!orderId}
            >
              {t("translation:deallocate")}
            </Button>
          )}
          <Button color="danger" onPress={onOpen} disabled={!orderId}>
            {t("translation:delete")}
          </Button>
        </div>
      </div>

      <h2 className="text-[16px] mb-6 text-neutral-smoke">
        Order Id: {order?.id}
      </h2>
      <InfoList
        header={t("translation:basic_info")}
        items={basicInfo}
        infoFields={infoFields}
      />
      <InfoList header={t("translation:order_metrics")} items={orderMetrics} />
      {dynamicFields.length > 0 && (
        <InfoList
          header={t("translation:dynamic_fields")}
          items={dynamicFields}
        />
      )}
      <ModelItem
        modelName={t("translation:order_lines")}
        items={getOrderLineInfo()}
      />
      <Modal
        disableAnimation={false}
        isOpen={isOpen}
        onOpenChange={onOpenChange}
        placement="top"
        aria-label="Confirm delete order"
      >
        {ModalBuilder({
          warning: true,
          modalHeader: t(
            "translation:confirm_delete_order",
            "Do you want to delete this order?",
          ),
          modalFooter: (
            <>
              <Button
                size="sm"
                color="default"
                variant="bordered"
                onPress={onOpenChange}
                className="hover:border-primary-default hover:text-primary-default"
              >
                {t("translation:do_not_delete")}
              </Button>
              <Button size="sm" color="danger" onPress={onConfirmDelete}>
                {t("translation:delete")}
              </Button>
            </>
          ),
        })}
      </Modal>
    </div>
  );
}
